import React, { useMemo } from 'react';

/**
 * OIM Hour-vs-Efficiency Heatmap (§7.1)
 *
 * Renders a date × hour-of-day grid colored by energy_intensity.
 * Greener = lower intensity (more efficient). Redder = higher intensity (less efficient).
 *
 * Pure CSS-grid implementation (no charting library). Pivots the existing
 * dashboard `series` client-side; no extra API call required.
 *
 * Props:
 *   data:    [{ timestamp, energy_intensity, status }]   — same shape returned by /api/oim/dashboard.series
 *   metric:  'energy_intensity' | 'cost_intensity'       — which field to plot
 *   label:   string                                       — tooltip / legend label
 */

const HOURS = Array.from({ length: 24 }, (_, h) => h);

const fmtDate = (d) => {
  // Day label like "Apr 29" (UTC; matches the API's UTC timestamps)
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${m[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2,'0')}`;
};

const isoDay = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;

/**
 * Map a value in [vMin, vMax] to a green->amber->red gradient.
 * Lower values are greener (better efficiency).
 * Returns a hex color string.
 */
const colorFor = (value, vMin, vMax) => {
  if (value == null || vMax === vMin) return '#e5e7eb'; // gray-200 for no-data / flat distributions
  const t = Math.max(0, Math.min(1, (value - vMin) / (vMax - vMin)));
  // 0 -> green (#10b981), 0.5 -> amber (#f59e0b), 1 -> red (#dc2626)
  // Blend in two segments
  const lerp = (a, b, x) => Math.round(a + (b - a) * x);
  let r, g, b;
  if (t < 0.5) {
    const k = t / 0.5;
    r = lerp(0x10, 0xf5, k);
    g = lerp(0xb9, 0x9e, k);
    b = lerp(0x81, 0x0b, k);
  } else {
    const k = (t - 0.5) / 0.5;
    r = lerp(0xf5, 0xdc, k);
    g = lerp(0x9e, 0x26, k);
    b = lerp(0x0b, 0x26, k);
  }
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
};

const Heatmap = ({ data = [], metric = 'energy_intensity', label = 'Energy Intensity (kWh/kL)' }) => {
  const { dates, grid, vMin, vMax, count } = useMemo(() => {
    const cells = new Map(); // key: `${isoDay}|${hour}` -> {value, status, ts}
    const dateSet = new Set();
    let _vMin = Infinity;
    let _vMax = -Infinity;
    let _count = 0;

    for (const row of data || []) {
      if (!row.timestamp) continue;
      const ts = new Date(row.timestamp);
      const day = isoDay(ts);
      const hour = ts.getUTCHours();
      const v = row[metric];
      dateSet.add(day);
      const key = `${day}|${hour}`;
      cells.set(key, { value: v, status: row.status, ts });
      if (typeof v === 'number') {
        if (v < _vMin) _vMin = v;
        if (v > _vMax) _vMax = v;
        _count += 1;
      }
    }

    const _dates = Array.from(dateSet).sort(); // ISO day strings sort lexicographically as date order
    return { dates: _dates, grid: cells, vMin: _vMin, vMax: _vMax, count: _count };
  }, [data, metric]);

  if (count === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-sm font-medium">No heatmap data</div>
          <div className="text-xs mt-1">Run compute over a longer period (try 7 days or 30 days)</div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        {/* Hour-of-day header */}
        <div className="flex items-center mb-1">
          <div className="w-16 shrink-0" />
          <div className="grid gap-0.5 flex-1" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
            {HOURS.map((h) => (
              <div key={h} className="text-[10px] text-gray-500 text-center font-mono">
                {h.toString().padStart(2, '0')}
              </div>
            ))}
          </div>
        </div>
        {/* Rows: one per date */}
        {dates.map((day) => {
          const dt = new Date(day + 'T00:00:00Z');
          return (
            <div key={day} className="flex items-center mb-0.5">
              <div className="w-16 shrink-0 text-xs text-gray-700 font-medium pr-2 text-right">
                {fmtDate(dt)}
              </div>
              <div className="grid gap-0.5 flex-1" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                {HOURS.map((h) => {
                  const cell = grid.get(`${day}|${h}`);
                  const v = cell?.value;
                  const bg = v == null ? '#f3f4f6' : colorFor(v, vMin, vMax);
                  const titleParts = [`${day} ${String(h).padStart(2,'0')}:00`];
                  if (v != null) titleParts.push(`${label}: ${typeof v === 'number' ? v.toFixed(4) : v}`);
                  if (cell?.status) titleParts.push(`Status: ${cell.status}`);
                  return (
                    <div
                      key={h}
                      className="h-6 rounded-sm border border-white"
                      style={{ backgroundColor: bg }}
                      title={titleParts.join(' · ')}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Legend */}
        <div className="flex items-center gap-2 mt-3 ml-16 text-xs text-gray-600">
          <span className="font-medium">{label}</span>
          <span>min {Number.isFinite(vMin) ? vMin.toFixed(3) : '—'}</span>
          <div className="flex h-3 w-24 rounded overflow-hidden border border-gray-200">
            {Array.from({ length: 12 }, (_, i) => {
              const t = i / 11;
              const v = vMin + (vMax - vMin) * t;
              return <div key={i} className="flex-1" style={{ backgroundColor: colorFor(v, vMin, vMax) }} />;
            })}
          </div>
          <span>max {Number.isFinite(vMax) ? vMax.toFixed(3) : '—'}</span>
          <span className="ml-2 text-gray-400">(green = more efficient)</span>
        </div>
      </div>
    </div>
  );
};

export default Heatmap;
